import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, ArrowLeft, Search, Star, Sparkles, 
  Volume2, RefreshCw, ChevronRight, GraduationCap, Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const VocabularyBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedLevel, setSelectedLevel] = useState('11plus');
  const [vocabulary, setVocabulary] = useState([]);
  const [rootWords, setRootWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWord, setSelectedWord] = useState(null);
  const [flashcardMode, setFlashcardMode] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [wordOfDay, setWordOfDay] = useState(null);

  useEffect(() => {
    const wordParam = searchParams.get('word');
    if (wordParam) setSearchQuery(wordParam);
    fetchVocabulary();
    fetchRootWords();
    fetchWordOfDay();
  }, [selectedLevel]);

  const fetchVocabulary = async () => {
    try {
      setLoading(true);
      const category = selectedLevel === 'gcse' ? 'tier_2_vocabulary' : 'core_vocabulary';
      const response = await axios.get(`${API}/api/elibrary/vocabulary?level=${selectedLevel}&category=${category}`);
      setVocabulary(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRootWords = async () => {
    try {
      const response = await axios.get(`${API}/api/elibrary/vocabulary?level=${selectedLevel}&category=root_words`);
      setRootWords(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchWordOfDay = async () => {
    try {
      // Check localStorage cache first (stays static for 24 hours)
      const cacheKey = `wordOfDay_${selectedLevel}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const hoursPassed = (Date.now() - timestamp) / (1000 * 60 * 60);
        // If less than 24 hours old, use cached data
        if (hoursPassed < 24) {
          setWordOfDay(data.word);
          return;
        }
      }
      
      // Fetch fresh data if no cache or cache expired
      const response = await axios.get(`${API}/api/elibrary/word-of-the-day`);
      setWordOfDay(response.data.word);
      
      // Cache the response with timestamp
      localStorage.setItem(cacheKey, JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filteredVocab = vocabulary.filter(word => 
    word.word?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    word.definition?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const difficultyColors = {
    easy: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
    medium: 'bg-amber-500/20 text-amber-600 border-amber-500/30',
    hard: 'bg-rose-500/20 text-rose-600 border-rose-500/30'
  };

  const shuffleCards = () => {
    const shuffled = [...vocabulary].sort(() => Math.random() - 0.5);
    setVocabulary(shuffled);
    setCurrentCard(0);
    setShowAnswer(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/elibrary')} className="text-slate-500 hover:text-slate-900">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50" />
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-slate-900">Vocabulary Builder</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {['11plus', 'gcse'].map((level) => (
                <Button
                  key={level}
                  variant={selectedLevel === level ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLevel(level)}
                  className={selectedLevel === level 
                    ? "bg-violet-600 text-white" 
                    : "border-slate-200 text-slate-500"
                  }
                >
                  {level === '11plus' ? '11+' : 'GCSE'}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Word of the Day Banner */}
        {wordOfDay && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-rose-600/20 border-amber-500/30">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-5 h-5 text-amber-600" />
                      <span className="text-amber-600 text-sm font-medium">Word of the Day</span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 capitalize mb-2">{wordOfDay.word}</h2>
                    <p className="text-slate-600 mb-2">{wordOfDay.definition}</p>
                    <p className="text-slate-500 italic text-sm">"{wordOfDay.example}"</p>
                  </div>
                  {wordOfDay.fun_fact && (
                    <div className="hidden md:flex items-start gap-2 max-w-xs">
                      <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-500">{wordOfDay.fun_fact}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Search and Mode Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search words..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-100 border-slate-200 text-slate-900 h-12"
            />
          </div>
          <Button
            variant={flashcardMode ? "default" : "outline"}
            onClick={() => {
              setFlashcardMode(!flashcardMode);
              setCurrentCard(0);
              setShowAnswer(false);
            }}
            className={flashcardMode ? "bg-violet-600" : "border-slate-200 text-slate-600"}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Flashcard Mode
          </Button>
        </div>

        <Tabs defaultValue="words" className="space-y-6">
          <TabsList className="bg-slate-100 border border-slate-200">
            <TabsTrigger value="words" className="data-[state=active]:bg-violet-600">Words ({filteredVocab.length})</TabsTrigger>
            <TabsTrigger value="roots" className="data-[state=active]:bg-violet-600">Root Words ({rootWords.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="words">
            {flashcardMode && filteredVocab.length > 0 ? (
              /* Flashcard View */
              <div className="max-w-xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-500 text-sm">Card {currentCard + 1} of {filteredVocab.length}</span>
                  <Button variant="ghost" size="sm" onClick={shuffleCards} className="text-slate-500">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Shuffle
                  </Button>
                </div>
                
                <motion.div
                  key={currentCard}
                  initial={{ opacity: 0, rotateY: -90 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  className="perspective-1000"
                >
                  <Card 
                    className="bg-gradient-to-br from-white to-slate-50 border-slate-200 cursor-pointer min-h-[300px] flex items-center justify-center"
                    onClick={() => setShowAnswer(!showAnswer)}
                  >
                    <CardContent className="p-8 text-center">
                      <AnimatePresence mode="wait">
                        {!showAnswer ? (
                          <motion.div
                            key="question"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <p className="text-3xl font-bold text-slate-900 mb-4 capitalize">
                              {filteredVocab[currentCard]?.word}
                            </p>
                            <p className="text-slate-500 text-sm">Click to reveal definition</p>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="answer"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="space-y-4"
                          >
                            <p className="text-xl text-slate-700">{filteredVocab[currentCard]?.definition}</p>
                            <p className="text-slate-500 italic">"{filteredVocab[currentCard]?.example}"</p>
                            <div className="flex flex-wrap justify-center gap-2 mt-4">
                              {filteredVocab[currentCard]?.synonyms?.map((syn) => (
                                <Badge key={syn} className="bg-emerald-500/20 text-emerald-600">{syn}</Badge>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
                
                <div className="flex justify-between mt-6">
                  <Button
                    variant="outline"
                    disabled={currentCard === 0}
                    onClick={() => { setCurrentCard(prev => prev - 1); setShowAnswer(false); }}
                    className="border-slate-200"
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={currentCard === filteredVocab.length - 1}
                    onClick={() => { setCurrentCard(prev => prev + 1); setShowAnswer(false); }}
                    className="bg-violet-600"
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : (
              /* Grid View */
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                  Array(6).fill(0).map((_, i) => (
                    <div key={i} className="h-40 rounded-xl bg-slate-100 animate-pulse" />
                  ))
                ) : (
                  filteredVocab.map((word, index) => (
                    <motion.div
                      key={word.word}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <Card 
                        className={`bg-slate-100 border-slate-200 hover:border-violet-500/30 transition-all cursor-pointer group ${
                          selectedWord === word.word ? 'border-violet-500/50 ring-1 ring-violet-500/30' : ''
                        }`}
                        onClick={() => setSelectedWord(selectedWord === word.word ? null : word.word)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-slate-900 font-semibold capitalize group-hover:text-violet-600 transition-colors">
                              {word.word}
                            </h3>
                            <Badge className={`${difficultyColors[word.difficulty]} border text-xs`}>
                              {word.difficulty}
                            </Badge>
                          </div>
                          
                          <p className="text-slate-500 text-sm line-clamp-2 mb-3">{word.definition}</p>
                          
                          <AnimatePresence>
                            {selectedWord === word.word && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="space-y-2 pt-2 border-t border-slate-200"
                              >
                                <p className="text-slate-600 italic text-sm">"{word.example}"</p>
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-xs text-slate-500">Synonyms:</span>
                                  {word.synonyms?.map((syn) => (
                                    <Badge key={syn} variant="outline" className="text-xs border-emerald-500/30 text-emerald-600">
                                      {syn}
                                    </Badge>
                                  ))}
                                </div>
                                {word.antonyms?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    <span className="text-xs text-slate-500">Antonyms:</span>
                                    {word.antonyms?.map((ant) => (
                                      <Badge key={ant} variant="outline" className="text-xs border-rose-500/30 text-rose-600">
                                        {ant}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="roots">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rootWords.map((root, index) => (
                <motion.div
                  key={root.root}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-slate-100 border-slate-200 hover:border-indigo-500/30 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl font-bold text-indigo-600">{root.root}</span>
                        <span className="text-slate-500">=</span>
                        <span className="text-slate-600">{root.meaning}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {root.examples?.map((ex) => (
                          <Badge key={ex} variant="outline" className="text-xs border-slate-200 text-slate-500">
                            {ex}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default VocabularyBuilder;
